import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Trophy, ArrowLeft, RefreshCw, Share2, Copy, Check, Send, MessageCircle,
} from 'lucide-react';

export const LeaderboardHeader = ({ isItalian, loadingData, fetchChampData, selectedChamp }) => {
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!showShareMenu) return;
    const handler = () => setShowShareMenu(false);
    const timer = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handler); };
  }, [showShareMenu]);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = selectedChamp
    ? (isItalian
        ? `Classifica live di ${selectedChamp.name} - EPT Italy eSports!`
        : `Live standings for ${selectedChamp.name} - EPT Italy eSports!`)
    : (isItalian ? 'Classifica Live - EPT Italy eSports!' : 'Live Standings - EPT Italy eSports!');

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
  };

  return (
    <header className="sticky top-0 z-50 bg-[#050508]/95 backdrop-blur-md border-b border-white/5">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors" data-testid="back-to-home">
          <ArrowLeft size={18} />
          <span className="text-sm font-medium hidden sm:inline">{isItalian ? 'Home' : 'Home'}</span>
        </Link>
        <div className="flex items-center gap-2">
          <Trophy size={20} className="text-[#FFEA00]" />
          <h1 className="font-heading text-lg sm:text-xl font-bold uppercase tracking-wider">
            {isItalian ? 'Classifica Live' : 'Live Standings'}
          </h1>
        </div>
        <div className="flex items-center gap-1">
          <div className="relative">
            <button onClick={() => setShowShareMenu(!showShareMenu)} className="text-slate-400 hover:text-[#FFEA00] transition-colors p-1" data-testid="share-button">
              <Share2 size={18} />
            </button>
            <AnimatePresence>
              {showShareMenu && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="absolute right-0 top-10 z-50 bg-[#0a0d15] border border-white/10 shadow-2xl w-52 overflow-hidden">
                  <p className="text-[10px] text-slate-500 uppercase tracking-widest px-3 pt-3 pb-1">{isItalian ? 'Condividi' : 'Share'}</p>
                  <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-[#25D366]/10 transition-colors text-sm" data-testid="share-whatsapp" onClick={() => setShowShareMenu(false)}>
                    <MessageCircle size={16} className="text-[#25D366]" /> WhatsApp
                  </a>
                  <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-[#229ED9]/10 transition-colors text-sm" data-testid="share-telegram" onClick={() => setShowShareMenu(false)}>
                    <Send size={16} className="text-[#229ED9]" /> Telegram
                  </a>
                  <a href={shareLinks.twitter} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/5 transition-colors text-sm" data-testid="share-twitter" onClick={() => setShowShareMenu(false)}>
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    X / Twitter
                  </a>
                  <button onClick={() => { handleCopyLink(); setShowShareMenu(false); }} className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-white/5 transition-colors text-sm w-full text-left border-t border-white/5" data-testid="share-copy">
                    {copied ? <Check size={16} className="text-[#00E676]" /> : <Copy size={16} className="text-slate-400" />}
                    {copied ? (isItalian ? 'Copiato!' : 'Copied!') : (isItalian ? 'Copia Link' : 'Copy Link')}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <button onClick={fetchChampData} className="text-slate-400 hover:text-[#00F0FF] transition-colors p-1" data-testid="refresh-standings">
            <RefreshCw size={18} className={loadingData ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
    </header>
  );
};
